import type {
  DataStoreDiagnosticStatus,
  DataStoreEntitySnapshot,
  DataStoreExtrasSnapshot,
} from "./Store"
import { DataStore } from "./Store"
import { readString } from "app-base/runtime/storage"

// Browser-console diagnostics live under the existing Logbook developer namespace:
// `logbook.dataLayer.status()`, `.entities("Log")`, `.query("Log", { dayId: 1 })`,
// `.extras()` and `.trace(true)`. The facade returns frozen snapshots only.
//
// #1707 (tracker #1677): the underlying inspector reads (`status`/`entities`/
// `query`/`extras`/`trace`) are all inherited generic `DataStore` methods (see
// `Store.ts`); this module's own job — Logbook's diagnostics *global naming* —
// is registering the inspector at `logbook.dataLayer` rather than app-base's
// generic top-level `dataLayer` slot, which is exactly the nested-namespace
// case app-base's own `registerDataLayerInspector` documents.
export interface DataLayerInspector {
  status(): DataStoreDiagnosticStatus;
  entities(type: string): readonly DataStoreEntitySnapshot[];
  query(type: string, filter: Readonly<Record<string, number | undefined>>): readonly DataStoreEntitySnapshot[];
  extras(): DataStoreExtrasSnapshot;
  trace(enabled: boolean): boolean;
}

interface LogbookDiagnosticsGlobal {
  logbook?: Record<string, unknown> & {
    dataLayer?: DataLayerInspector;
  };
}

interface InspectorRegistrations {
  base: DataLayerInspector | undefined;
  inspectors: DataLayerInspector[];
}

const registrations = new WeakMap<object, InspectorRegistrations>();
const traceStorageKey = "logbook-data-layer-trace";

export function createDataLayerInspector(store: DataStore): DataLayerInspector {
  store.reportDiagnosticsAttached();
  if (readString(traceStorageKey, { storage: localStorage }) == "1") {
    store.setDiagnosticTrace(true);
  }
  return Object.freeze({
    status: () => store.getDiagnosticStatus(),
    entities: (type: string) => store.getDiagnosticEntities(type),
    query: (type: string, filter: Readonly<Record<string, number | undefined>>) =>
      store.getDiagnosticQuery(type, filter),
    extras: () => store.getDiagnosticExtras(),
    trace: (enabled: boolean) => {
      if (enabled) {
        localStorage.setItem(traceStorageKey, "1");
      } else {
        localStorage.removeItem(traceStorageKey);
      }
      return store.setDiagnosticTrace(enabled);
    },
  });
}

export function registerDataLayerInspector(
  inspector: DataLayerInspector,
  target: LogbookDiagnosticsGlobal = globalThis as LogbookDiagnosticsGlobal,
): () => void {
  const logbook = target.logbook ?? {};
  target.logbook = logbook;
  let state = registrations.get(target);
  if (!state) {
    state = { base: logbook.dataLayer, inspectors: [] };
    registrations.set(target, state);
  }
  state.inspectors.push(inspector);
  logbook.dataLayer = inspector;
  return () => {
    const index = state.inspectors.lastIndexOf(inspector);
    if (index === -1) {
      return;
    }
    state.inspectors.splice(index, 1);
    const active = state.inspectors.at(-1) ?? state.base;
    if (active) {
      logbook.dataLayer = active;
    } else {
      delete logbook.dataLayer;
    }
    if (state.inspectors.length === 0) {
      registrations.delete(target);
    }
  };
}
